import { useEffect, useState } from 'react';
import type { SignDocumentDetail, SignSourceFile } from '@todo/shared';
import { ApiError } from '../api/client';
import { signApi } from '../api/sign.api';

/**
 * The files merged into a CG Sign envelope, in order - the sender can drag
 * them (or use the arrows) into a new order and remove one, then Apply.
 * Nothing changes until Apply: the API rebuilds the merged pdf once, and the
 * placed signature boxes follow their pages.
 *
 * Only while nobody has signed. A DRAFT is editable here; a SENT envelope
 * with no signatures is told to Recall first, because a link already in a
 * signer's hands must not see the pages move.
 *
 * An envelope made before the source files were kept comes back
 * `managed: false`: it can still have files added at the end (Add files),
 * but there is nothing to reorder.
 */
export function SignFileList({
  doc, canEdit, nobodySigned, busy, onApply,
}: {
  doc: SignDocumentDetail;
  /** The sender, on a DRAFT. */
  canEdit: boolean;
  nobodySigned: boolean;
  busy: boolean;
  /** The ids to keep, in order. The parent runs it and refreshes the document. */
  onApply: (order: string[]) => Promise<void>;
}) {
  const [files, setFiles] = useState<SignSourceFile[] | null>(null);
  const [managed, setManaged] = useState(true);
  const [items, setItems] = useState<SignSourceFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  // A change of any kind (apply, add files, recall) writes an event, so the
  // event count is the cue to read the list again.
  useEffect(() => {
    if (!canEdit) return;
    let live = true;
    setError(null);
    signApi.sourceFiles(doc.id)
      .then((res) => {
        if (!live) return;
        setManaged(res.managed);
        setFiles(res.files);
        setItems(res.files);
      })
      .catch((err) => {
        if (live) setError(err instanceof ApiError ? err.message : 'The file list could not be loaded.');
      });
    return () => { live = false; };
  }, [canEdit, doc.id, doc.status, doc.fileCount, doc.events.length]);

  if (!canEdit) {
    return doc.status === 'SENT' && nobodySigned ? (
      <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500 ring-1 ring-slate-200">
        To change the files, recall the document to a draft first.
      </p>
    ) : null;
  }
  if (error) return <p className="text-xs text-red-600">{error}</p>;
  if (files === null) return <p className="text-xs text-slate-400">Loading files...</p>;
  if (!managed) {
    return (
      <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500 ring-1 ring-slate-200">
        This document was created before files could be reordered. You can still add files, at the end.
      </p>
    );
  }

  const original = files.map((f) => f.id);
  const changed = items.length !== original.length || items.some((f, i) => f.id !== original[i]);
  const removing = items.length < original.length;

  function move(from: number, to: number) {
    if (to < 0 || to >= items.length || from === to) return;
    const next = [...items];
    const [picked] = next.splice(from, 1);
    next.splice(to, 0, picked!);
    setItems(next);
  }

  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
        Files in this document
      </p>
      <ol className="mt-2 space-y-1.5">
        {items.map((f, i) => (
          <li
            key={f.id}
            draggable={!busy}
            onDragStart={() => setDragIndex(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => { if (dragIndex !== null) move(dragIndex, i); setDragIndex(null); }}
            onDragEnd={() => setDragIndex(null)}
            className={[
              'flex items-center gap-2 rounded-lg border bg-white px-2.5 py-2 text-xs',
              dragIndex === i ? 'border-brand-400 opacity-60' : 'border-slate-200',
            ].join(' ')}
          >
            <span className="cursor-grab select-none text-slate-300" aria-hidden="true">⋮⋮</span>
            <span className="w-5 text-slate-400 tabular-nums">{i + 1}.</span>
            <span className="min-w-0 flex-1 truncate font-medium text-slate-700" title={f.fileName}>
              {f.fileName}
            </span>
            <span className="shrink-0 text-slate-400">
              {f.pageCount} {f.pageCount === 1 ? 'page' : 'pages'}
            </span>
            <button type="button" disabled={busy || i === 0} onClick={() => move(i, i - 1)}
              aria-label={`Move ${f.fileName} up`}
              className="rounded px-1.5 py-0.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30">↑</button>
            <button type="button" disabled={busy || i === items.length - 1} onClick={() => move(i, i + 1)}
              aria-label={`Move ${f.fileName} down`}
              className="rounded px-1.5 py-0.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30">↓</button>
            <button type="button" disabled={busy || items.length === 1}
              onClick={() => setItems(items.filter((x) => x.id !== f.id))}
              aria-label={`Remove ${f.fileName}`}
              title={items.length === 1 ? 'A document needs at least one file' : 'Remove this file'}
              className="rounded px-1.5 py-0.5 text-red-500 hover:bg-red-50 disabled:opacity-30">✕</button>
          </li>
        ))}
      </ol>
      {removing && (
        <p className="mt-2 text-xs text-amber-700">
          Removing a file also removes its pages and any signature boxes placed on them.
        </p>
      )}
      {changed && (
        <div className="mt-2 flex gap-2">
          <button type="button" disabled={busy}
            onClick={() => void onApply(items.map((f) => f.id))}
            className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-700 disabled:opacity-50">
            Apply changes
          </button>
          <button type="button" disabled={busy} onClick={() => setItems(files)}
            className="rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-100 disabled:opacity-50">
            Reset
          </button>
        </div>
      )}
    </div>
  );
}
